import { Plus, Save, Trash2 } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import Banner from "@buildpy/ui/banner";
import EmptyState from "@buildpy/ui/empty-state";
import Field from "@/components/field";
import Money from "@/components/money";
import { UNSAVED_LEAVE_CONFIRM } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { formatNumber, formatQty, formatWon, parseMoney, parseQty } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { cn } from "@/lib/utils";
import { createChangeOrder, readContractPayments, saveChangeOrderDraft } from "@/api/actions/change-order";
import { calcChangeOrder } from "@buildpy/core/modules/change-order/calc";
import { CHANGE_ORDER_REV_STALE_NOTICE } from "@buildpy/core/modules/change-order/messages";
import type { ChangeOrderInput } from "@buildpy/core/modules/change-order/prepare";
import { CHANGE_ORDER_SETTLEMENT_LABEL, reallocatePayments } from "@buildpy/core/modules/change-order/reallocate";
import type { SourcePickerItem, SourcePickerTrade } from "@buildpy/core/modules/change-order/source";
import { CHANGE_ORDER_DIRECTION_LABEL, CHANGE_ORDER_DIRECTIONS, type ChangeOrderDirection } from "@buildpy/core/modules/change-order/types";
import {
  CHANGE_ORDER_FREE_LINE_LABEL,
  changeOrderPath,
  clearOverOnEdit,
  reloadPlanOf,
  samePaymentRows,
  singleFlight,
} from "@buildpy/core/modules/change-order/view";
import type { ContractPayment } from "@buildpy/core/modules/contract/rows";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import { vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";

// 잔량 초과 토스트 자리 — 행 아래 경고가 걷히면 같이 내린다(남아서 「만들기」를 덮지 않게).
const OVER_TOAST = "change-order-over";

type Mode =
  | { kind: "create"; contractId: string }
  | { kind: "edit"; contractId: string; changeOrderId: string; rev: number };

interface Props {
  mode: Mode;
  initial: ChangeOrderInput;
  // 지금 계약금액(원금액 + 승인 증감) — 변경 후 금액을 바로 보여 준다.
  currentTotal: number;
  // 연결된 일지 한 줄(있으면).
  sourceLogLabel?: string | null;
  // 유지보수 현장은 false — 공기 증감 칸을 숨기고 저장 때 비운다(저장소도 거절한다).
  extendAllowed?: boolean;
  // 계약 지급회차 — 미지급 회차가 없을 때 붙을 정산 회차를 저장소와 같은 reallocatePayments 로 미리 보인다.
  payments?: readonly Pick<ContractPayment, "id" | "seq" | "ratio_bp" | "amount" | "paid_at">[];
  // 계약 견적의 공종 → 맨 끝 항목 — 「견적 줄에서 고르기」 목록(없으면 고르기를 숨긴다).
  sourceTrades?: readonly SourcePickerTrade[];
  // 계약의 부가세 표기 — 별도 계약이면 단가가 공급가라 증감에 부가세를 얹는다.
  vatMode?: VatMode;
}

interface Row {
  key: number;
  direction: ChangeOrderDirection;
  item: string;
  unit: string;
  qty: string;
  material: string;
  labor: string;
  note: string;
  // 원 견적 줄 — null 이면 「추가 항목」.
  source: string | null;
  // 고르기 칸에서 먼저 고른 공종.
  pickTrade: string;
  // 고르기 칸을 펼쳤다(감액은 기본으로 펼친다).
  picking: boolean;
}

const moneyText = (n: number): string => (n === 0 ? "" : formatNumber(n));

// 키는 편집기마다 0 부터 — 모듈 전역 카운터는 서버에서 요청마다 누적돼 name 이 하이드레이션과 어긋난다.
const rowOf = (key: number, it?: ChangeOrderInput["items"][number]): Row => ({
  key,
  direction: it?.direction ?? "ADD",
  item: it?.item ?? "",
  unit: it?.unit ?? "식",
  qty: it ? String(it.qty) : "1",
  material: moneyText(it?.material_unit_price ?? 0),
  labor: moneyText(it?.labor_unit_price ?? 0),
  note: it?.note ?? "",
  source: it?.source_quote_item_id ?? null,
  pickTrade: "",
  picking: false,
});

// 원 견적 줄을 고르면 항목·단위·수량·단가를 그 줄로 채운다(단위는 연결된 동안 고칠 수 없고 서버도 원 줄 단위로 저장한다).
const pickedRow = (picked: SourcePickerItem, tradeId: string): Partial<Row> => ({
  source: picked.id,
  pickTrade: tradeId,
  picking: false,
  item: picked.item,
  unit: picked.unit,
  qty: String(picked.qty),
  material: moneyText(picked.material_unit_price),
  labor: moneyText(picked.labor_unit_price),
});

const lineOf = (r: Row) => ({
  direction: r.direction,
  item: r.item,
  unit: r.unit,
  qty: parseQty(r.qty),
  material_unit_price: parseMoney(r.material),
  labor_unit_price: parseMoney(r.labor),
  note: r.note,
  source_quote_item_id: r.source,
});

// 공기 증감 입력 — 비었거나 "-" 만이면 없음, 앞의 "-" 는 단축.
const extendDaysOf = (text: string): number | null => (text === "" || text === "-" ? null : Number(text));

// 계약 분할 없음 — 렌더마다 새 배열이면 정산 useMemo 가 매번 다시 돈다.
const NO_PAYMENTS: NonNullable<Props["payments"]> = [];
const NO_TRADES: NonNullable<Props["sourceTrades"]> = [];

// 변경공사 항목 편집 — 카드 한 장이 한 줄. 금액은 저장소와 같은 calcChangeOrder 로 바로 보인다.
const ChangeOrderEditor = ({
  mode,
  initial,
  currentTotal,
  sourceLogLabel,
  extendAllowed = true,
  payments = NO_PAYMENTS,
  sourceTrades = NO_TRADES,
  vatMode = "INCL",
}: Props) => {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [rev, setRev] = useState(mode.kind === "edit" ? mode.rev : 0);
  const revRef = useRef(rev);
  // 내 저장 중·저장 횟수 — 저장과 겹친 다시 읽기가 내 rev 상승을 「다른 곳의 저장」으로 오판하지 않게.
  const savingRef = useRef(false);
  const saveSeqRef = useRef(0);
  // 저장과 겹쳐 rev 를 못 본 다시 읽기 — 저장이 끝나면 한 번 더 읽는다.
  const recheckRef = useRef(false);
  const reloadRef = useRef<(() => Promise<void>) | null>(null);
  const changeOrderId = mode.kind === "edit" ? mode.changeOrderId : null;
  const [title, setTitle] = useState(initial.title);
  const [reason, setReason] = useState(initial.reason);
  const [extendDays, setExtendDays] = useState(initial.extend_days ? String(initial.extend_days) : "");
  const [rows, setRows] = useState<Row[]>(() => initial.items.map((it, i) => rowOf(i, it)));
  const nextKey = useRef(initial.items.length);
  // 화면 글자 그대로 견준다(고르기 칸 펼침은 빼고) — 만들기·저장 전에 메뉴·뒤로·새로고침으로 떠나면 묻는다.
  const snapshot = JSON.stringify({
    title,
    reason,
    extendDays,
    rows: rows.map((r) => [r.direction, r.item, r.unit, r.qty, r.material, r.labor, r.note, r.source]),
  });
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot);
  const dirty = snapshot !== savedSnapshot;
  useLeaveGuard({ unload: dirty, links: dirty, back: dirty, message: UNSAVED_LEAVE_CONFIRM });
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);
  // 다른 곳에서 이 변경공사가 저장됐는데 고친 게 있어 새로고침하지 못했다 — 옆 미리보기가 옛 값이다.
  const [stale, setStale] = useState(false);
  // 잔량을 넘은 원 견적 줄 — 그 줄을 가리키는 항목 아래에 서버 문구를 띄운다.
  const [over, setOver] = useState<{ source: string; message: string } | null>(null);
  useEffect(() => {
    if (!over) toast.dismiss(OVER_TOAST);
  }, [over]);
  const sourceById = useMemo(
    () => new Map(sourceTrades.flatMap((t) => t.items.map((i) => [i.id, { item: i, trade: t }] as const))),
    [sourceTrades]
  );
  const canPick = sourceTrades.length > 0;
  // 지급회차는 편집 중에도 다른 창에서 「받음」이 바뀔 수 있다 — 창으로 돌아올 때 다시 읽어 정산 미리보기를 맞춘다.
  const [livePayments, setLivePayments] = useState(payments);
  const liveRef = useRef(payments);
  // 지금 계약금액도 그새 다른 변경공사 승인으로 바뀔 수 있다 — 회차와 같이 다시 읽는다(편집 중인 항목은 건드리지 않는다).
  const [liveTotal, setLiveTotal] = useState(currentTotal);
  const liveTotalRef = useRef(currentTotal);
  useEffect(() => {
    revRef.current = rev;
  }, [rev]);
  useEffect(() => {
    liveRef.current = payments;
    setLivePayments(payments);
  }, [payments]);
  useEffect(() => {
    liveTotalRef.current = currentTotal;
    setLiveTotal(currentTotal);
  }, [currentTotal]);
  useEffect(() => {
    // 지급회차가 없어도 저장된 변경공사는 rev 를 본다 — 다른 곳의 저장으로 미리보기가 옛 값이 되는 것을 놓치지 않게.
    if (payments.length === 0 && changeOrderId === null) return;
    let alive = true;
    const reload = singleFlight(async () => {
      if (document.visibilityState !== "visible") return;
      const saveSeq = saveSeqRef.current;
      try {
        const result = await readContractPayments(mode.contractId, changeOrderId ?? undefined);
        if (!alive || !result.ok) return;
        const pending = savingRef.current || saveSeqRef.current !== saveSeq;
        // 무엇이 바뀌었는지로 문구를 고른다 — 계약금액만 바뀌었는데 「지급 기록」 이라 하지 않게.
        const plan = reloadPlanOf({
          editorRev: changeOrderId === null ? null : revRef.current,
          serverRev: result.changeOrderRev,
          paymentsChanged: !samePaymentRows(liveRef.current, result.payments),
          totalChanged: liveTotalRef.current !== result.currentTotal,
          dirty: dirtyRef.current,
          pending,
        });
        if (pending) {
          if (savingRef.current) recheckRef.current = true;
          else window.setTimeout(reload, 0);
        }
        if (plan.stale) setStale(true);
        if (!plan.notice) return;
        liveRef.current = result.payments;
        liveTotalRef.current = result.currentTotal;
        setLivePayments(result.payments);
        setLiveTotal(result.currentTotal);
        toast.info(plan.notice);
        // 옆의 서버 표(변경 전 → 후·지급 상태)도 맞춘다 — 다른 창이 이 초안을 저장했는데 고친 게 있으면 재마운트로 잃으니 stale 안내만 띄운다.
        if (plan.refresh) router.refresh();
      } catch {
        // 세션 만료·네트워크 실패는 미리보기만 옛값으로 둔다 — 저장 때 서버가 다시 막는다.
      }
    });
    reloadRef.current = reload;
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    return () => {
      alive = false;
      reloadRef.current = null;
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
    };
  }, [mode.contractId, changeOrderId, payments.length, router]);

  const calc = useMemo(() => {
    try {
      return calcChangeOrder(rows.map(lineOf), vatMode);
    } catch {
      return null;
    }
  }, [rows, vatMode]);

  // 새로 붙을 정산 회차 금액 — 없거나(미지급 회차가 받음·증감 0) 계산이 막히면(기지급 미만 등) null.
  const settlement = useMemo(() => {
    if (!calc || livePayments.length === 0) return null;
    try {
      const added = reallocatePayments(livePayments, liveTotal + calc.delta_total, () => "settlement").find((p) => p.new);
      return added ? added.amount : null;
    } catch {
      return null;
    }
  }, [calc, liveTotal, livePayments]);

  const clearOver = () => {
    setOver(null);
    toast.dismiss(OVER_TOAST);
  };

  const clearOverFor = (key: number) => {
    const next = clearOverOnEdit(over, rows, key, () => toast.dismiss(OVER_TOAST));
    if (next !== over) setOver(next);
  };

  const patch = (key: number, next: Partial<Row>) => {
    clearOverFor(key);
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...next } : r)));
  };

  const save = async () => {
    const sent = snapshot;
    const days = extendAllowed ? extendDaysOf(extendDays) : null;
    const input: ChangeOrderInput = {
      title,
      reason,
      source_log_id: initial.source_log_id ?? null,
      extend_days: days,
      items: rows.map(lineOf),
    };
    // 시작에서 내리지 않는다 — 내리는 중인 같은 id 를 다시 띄우면 sonner 가 삭제 표시를 물려받아 안 보인다.
    const failed = (message: string, source: string | undefined) => {
      if (source) {
        setOver({ source, message });
        toast.error(message, { id: OVER_TOAST });
        return;
      }
      clearOver();
      toast.error(message);
    };
    savingRef.current = true;
    setPending(true);
    try {
      if (mode.kind === "create") {
        const result = await createChangeOrder(mode.contractId, input);
        if (!result.ok) {
          failed(result.message, result.over_source);
          return;
        }
        clearOver();
        toast.success("변경공사를 만들었습니다");
        setSavedSnapshot(sent);
        router.push(changeOrderPath(mode.contractId, result.change_order_id));
        return;
      }
      const result = await saveChangeOrderDraft(mode.changeOrderId, input, rev);
      if (!result.ok) {
        failed(result.message, result.over_source);
        return;
      }
      clearOver();
      setRev(result.rev);
      revRef.current = result.rev;
      setSavedSnapshot(sent);
      toast.success("저장했습니다");
      router.refresh();
    } catch (error) {
      if (settleRedirect(error)) return;
      toast.error("저장하지 못했습니다");
    } finally {
      savingRef.current = false;
      saveSeqRef.current += 1;
      if (recheckRef.current) {
        recheckRef.current = false;
        window.setTimeout(() => void reloadRef.current?.(), 0);
      }
      setPending(false);
    }
  };

  return (
    <SectionCard className="flex flex-col gap-y-3 text-sm" padding="sm" data-testid="change-order-editor">
      <p className="sr-only" data-testid="change-order-preview-stale-live" role="status">
        {stale ? CHANGE_ORDER_REV_STALE_NOTICE : ""}
      </p>
      {stale && (
        <div aria-hidden="true" data-testid="change-order-preview-stale">
          <Banner tone="warning">{CHANGE_ORDER_REV_STALE_NOTICE}</Banner>
        </div>
      )}
      {sourceLogLabel && (
        <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">연결된 일지 · {sourceLogLabel}</p>
      )}

      <Field label="제목">
        <Input
          aria-label="제목"
          inputSize="sm"
          maxLength={200}
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field label="사유">
        <Textarea
          aria-label="사유"
          className="min-h-20 text-sm"
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {extendAllowed ? (
        <Field className="w-40" label="공기 증감(작업일)">
          <Input
            className="text-right tabular-nums"
            inputSize="sm"
            name="extend_days"
            placeholder="0"
            value={extendDays}
            onChange={(e) => setExtendDays(e.target.value.replace(/[^\d-]/g, "").replace(/(?!^)-/g, ""))}
            data-testid="change-order-extend-days"
          />
        </Field>
      ) : (
        <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground" data-testid="change-order-extend-off">
          유지보수 중인 현장이라 공기 증감은 받지 않습니다 — 금액 항목만 적어 주세요
        </p>
      )}

      <div className="flex flex-col gap-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground">항목</h3>
        {rows.length === 0 && (
          <EmptyState>
            {extendAllowed ? "항목이 없습니다. 금액 변경 없이 공기 증감만 기록할 수도 있습니다." : "항목이 없습니다."}
          </EmptyState>
        )}
        {rows.map((r, i) => {
          const line = calc?.lines[i];
          return (
            <div key={r.key} className="flex flex-col gap-y-2 rounded-md border border-border p-2" data-testid="change-order-row">
              <div className="flex items-center justify-between gap-x-2">
                <div className="inline-flex rounded-md border border-border p-0.5" role="radiogroup">
                  {CHANGE_ORDER_DIRECTIONS.map((d) => (
                    <button
                      key={d}
                      aria-checked={r.direction === d}
                      className={cn(
                        "tap-target relative rounded-md px-2 py-0.5 text-xs",
                        r.direction === d ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                      )}
                      role="radio"
                      type="button"
                      onClick={() => patch(r.key, { direction: d, picking: d === "DEDUCT" && canPick && !r.source })}>
                      {CHANGE_ORDER_DIRECTION_LABEL[d]}
                    </button>
                  ))}
                </div>
                <span className="ml-auto tabular-nums">{line ? <Money signed value={line.signed_amount} /> : "—"}</span>
                <Button
                  aria-label="항목 지우기"
                  className="-m-1"
                  size="icon-xs"
                  type="button"
                  variant="ghost-destructive"
                  onClick={() => {
                    clearOverFor(r.key);
                    setRows((prev) => prev.filter((x) => x.key !== r.key));
                  }}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
              <SourcePicker
                canPick={canPick}
                linked={r.source ? sourceById.get(r.source) ?? null : null}
                over={over && r.source === over.source ? over.message : null}
                row={r}
                trades={sourceTrades}
                onChange={(next) => patch(r.key, next)}
              />
              <div className="grid grid-cols-[1fr_5rem] gap-2">
                <Input
                  aria-label="항목"
                  inputSize="sm"
                  name={`item-${r.key}`}
                  placeholder="항목 (예: 욕실 타일 등급 상향)"
                  value={r.item}
                  onChange={(e) => patch(r.key, { item: e.target.value })}
                />
                {r.source ? (
                  <Input
                    aria-label="단위"
                    inputSize="sm"
                    disabled
                    name={`unit-${r.key}`}
                    title="원 견적 줄의 단위를 씁니다"
                    value={sourceById.get(r.source)?.item.unit ?? r.unit}
                    data-testid="change-order-unit-linked"
                  />
                ) : (
                  <Input
                    aria-label="단위"
                    inputSize="sm"
                    name={`unit-${r.key}`}
                    placeholder="단위"
                    value={r.unit}
                    onChange={(e) => patch(r.key, { unit: e.target.value })}
                  />
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field className="gap-y-0.5" label="수량">
                  <Input
                    className="text-right tabular-nums"
                    inputMode="decimal"
                    inputSize="sm"
                    name={`qty-${r.key}`}
                    value={r.qty}
                    onChange={(e) => patch(r.key, { qty: e.target.value })}
                  />
                </Field>
                <Field className="gap-y-0.5" label="재료 단가">
                  <Input
                    className="text-right tabular-nums"
                    inputMode="numeric"
                    inputSize="sm"
                    name={`material-${r.key}`}
                    value={r.material}
                    onChange={(e) => patch(r.key, { material: moneyText(parseMoney(e.target.value)) })}
                  />
                </Field>
                <Field className="gap-y-0.5" label="노무 단가">
                  <Input
                    className="text-right tabular-nums"
                    inputMode="numeric"
                    inputSize="sm"
                    name={`labor-${r.key}`}
                    value={r.labor}
                    onChange={(e) => patch(r.key, { labor: moneyText(parseMoney(e.target.value)) })}
                  />
                </Field>
              </div>
              <Input
                aria-label="비고"
                inputSize="sm"
                name={`note-${r.key}`}
                placeholder="비고"
                value={r.note}
                onChange={(e) => patch(r.key, { note: e.target.value })}
              />
            </div>
          );
        })}
        <Button className="self-start" size="sm" type="button" variant="outline" onClick={() => {
            const key = nextKey.current++;
            setRows((prev) => [...prev, rowOf(key)]);
          }}>
          <Plus />
          항목 추가
        </Button>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-border pt-2" data-testid="change-order-editor-total">
        <dt className="text-muted-foreground">단가 기준</dt>
        <dd className="text-right" data-testid="change-order-editor-vat-basis">{vatBasisLabel(vatMode)}</dd>
        {vatMode === "EXCL" && (
          <>
            <dt className="text-muted-foreground">공급가 증감</dt>
            <dd className="text-right tabular-nums">{calc ? <Money signed value={calc.delta_supply} /> : "—"}</dd>
            <dt className="text-muted-foreground">부가세 10%</dt>
            <dd className="text-right tabular-nums">{calc ? <Money signed value={calc.delta_vat} /> : "—"}</dd>
          </>
        )}
        <dt className="text-muted-foreground">증감(부가세 포함)</dt>
        <dd className="text-right tabular-nums">{calc ? <Money signed value={calc.delta_total} /> : "—"}</dd>
        <dt className="text-muted-foreground">변경 후 계약금액</dt>
        <dd className="text-right font-semibold tabular-nums">{calc ? <Money value={liveTotal + calc.delta_total} /> : "—"}</dd>
        {settlement !== null && (
          <>
            <dt className="text-muted-foreground">{CHANGE_ORDER_SETTLEMENT_LABEL} 회차(추가)</dt>
            <dd className="text-right tabular-nums" data-testid="change-order-editor-settlement">
              <Money value={settlement} />
            </dd>
            <dd className="col-span-2 text-xs text-muted-foreground">미지급 회차가 없어 늘어난 금액은 끝에 새 회차로 붙습니다.</dd>
          </>
        )}
      </dl>

      <div className="flex justify-end">
        <Button disabled={pending} size="sm" type="button" variant="primary" onClick={() => void save()}>
          <Save />
          {mode.kind === "create" ? "만들기" : "저장"}
        </Button>
      </div>
    </SectionCard>
  );
};

interface SourcePickerProps {
  row: Row;
  trades: readonly SourcePickerTrade[];
  canPick: boolean;
  linked: { item: SourcePickerItem; trade: SourcePickerTrade } | null;
  over: string | null;
  onChange: (next: Partial<Row>) => void;
}

const pickedText = (i: SourcePickerItem): string =>
  [i.label, i.space_label, `${formatQty(i.qty)}${i.unit}`, formatWon(i.amount)].filter(Boolean).join(" · ");

// 「견적 줄에서 고르기」 — 공종을 고르고 그 공종의 맨 끝 항목을 고른다 · 연결 없으면 「추가 항목」.
const SourcePicker = ({ row, trades, canPick, linked, over, onChange }: SourcePickerProps) => {
  const trade = trades.find((t) => t.id === row.pickTrade) ?? null;
  return (
    <div className="flex flex-col gap-y-1.5" data-testid="change-order-source">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {row.source ? (
          <span className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground" data-testid="change-order-source-label">
            원 견적: {linked ? [linked.trade.name, linked.item.space_label, linked.item.label].filter(Boolean).join(" · ") : "견적 줄"}
          </span>
        ) : (
          <span className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground" data-testid="change-order-source-free">
            {CHANGE_ORDER_FREE_LINE_LABEL}
          </span>
        )}
        {canPick && !row.picking && (
          <Button size="sm" type="button" variant="ghost" onClick={() => onChange({ picking: true })}>
            {row.source ? "다른 견적 줄 고르기" : "견적 줄에서 고르기"}
          </Button>
        )}
        {row.source && (
          <Button size="sm" type="button" variant="ghost" onClick={() => onChange({ source: null, picking: false })}>
            연결 끊기
          </Button>
        )}
      </div>
      {canPick && row.picking && (
        <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
          <SimpleSelect
            aria-label="공종"
            options={[{ value: "", label: "공종 고르기" }, ...trades.map((t) => ({ value: t.id, label: t.name }))]}
            size="sm"
            value={row.pickTrade}
            onValueChange={(v) => onChange({ pickTrade: v })}
          />
          <SimpleSelect
            aria-label="원 견적 항목"
            disabled={!trade}
            options={[
              { value: "", label: trade ? "항목 고르기" : "공종을 먼저 고르세요" },
              ...(trade?.items ?? []).map((i) => ({ value: i.id, label: pickedText(i) })),
            ]}
            size="sm"
            value={row.source && trade?.items.some((i) => i.id === row.source) ? row.source : ""}
            onValueChange={(v) => {
              const picked = trade?.items.find((i) => i.id === v);
              if (picked && trade) onChange(pickedRow(picked, trade.id));
            }}
          />
        </div>
      )}
      {over && (
        <p className="text-xs text-destructive" role="alert" data-testid="change-order-source-over">
          {over}
        </p>
      )}
    </div>
  );
};

export default ChangeOrderEditor;
